import { MODAL_STATE } from 'tradera/constants/ui';
import { nullifyUndefinedProperties } from 'tradera/utils/object';
import { cache, CACHE_KEYS } from 'tradera/utils/cache';
import { initialize } from './reducer';
import { type Dispatch } from 'redux';
import { type InitData } from '../init-data/types';
import type { QueryParams } from 'tradera/apps/tradera/components/discover/components/filters/types';
import type {
    SystemMessage,
    SystemMessageType,
} from '../services/types/webapi-member-generated';
import type {
    MyTraderaPurchaseOrderApiDto,
    MyTraderaPurchaseOrderRowApiDto,
    SellerSoldExtendedLifePurchaseOrderRowApiDto,
    SystemBadge,
    SystemBadgeType,
} from '../services/types/webapi-mytradera-generated';
import type { TranslationKeys } from 'tradera/components/share-modals/share-modal';
import { type ProductCommunication } from 'tradera/apps/tradera/components/communication-banner/constant';

export type UiState = {
    categoryDropdownMenu: {
        open: MODAL_STATE;
    };
    badges: {
        messageNotificationCount: number;
        badgeNotificationCount: number;
    };
    itemSettings: {
        open: MODAL_STATE;
        item: unknown;
    };
    newMessageModal: {
        open: MODAL_STATE;
        title: string;
        senderId: number;
        recipientId: number;
        recipientAlias: string;
        itemId: number;
        itemTitle: string;
        itemImageUrl: string;
        itemSellerId: number;
        purchaseOrderId: string;
    };

    modals: {
        // currentModal: unknown;
        // currentModalData: { [key: string]: string } | null;

        currentModal: string | null;
        currentModalData:
            | ({ [key: string]: string } & {
                  itemIds: number[];
                  queryParams: QueryParams;
                  purchaseOrder: MyTraderaPurchaseOrderApiDto;
                  items: MyTraderaPurchaseOrderRowApiDto[];
                  extendedLifeItems: SellerSoldExtendedLifePurchaseOrderRowApiDto[];
              })
            | null;
    };
    notificationOptinModal: {
        open: MODAL_STATE;
        modalType: string;
        gaLabel: string;
        title: string;
        description: string;
        tagGroups: unknown[];
    };
    profileMenu: {
        open: MODAL_STATE;
        redirectUrl: string | undefined;
    };
    siteLayout: string;
    showHeaderSearch: boolean;
    showSaveSearchBanner: boolean;
    showSearchForm: boolean;
    systemMessages: Record<SystemMessageType, SystemMessage>;
    systemBadges: Record<SystemBadgeType, SystemBadge>;
    videoModal: {
        open: MODAL_STATE;
        videoUrl: string;
        videoId: string;
        title: string;
        pageType: string;
        buttonLink: string;
        buttonText: string;
    };
    shareModal: {
        isOpen: boolean;
        settings:
            | ({ [key: string]: string } & {
                  translationKeys: TranslationKeys;
              })
            | null;
    };
    wishListSideSheet: {
        isOpen: boolean;
    };
    notificationsSideSheet: {
        isOpen: boolean;
    };
    productCommunications: ProductCommunication[];
    collectSsnModal: {
        isOpen: boolean;
    };
    referralModal: {
        isOpen: boolean;
    };
    showCollectSsnModal: boolean;
    helpSection: {
        isOpen: boolean;
        settings: unknown;
    };
    showAdBlockerMessage: boolean;
};

export const getInitialBadges = (
    messageNotificationCount?: number,
    badgeNotificationCount?: number,
) => ({
    messageNotificationCount: messageNotificationCount || 0,
    badgeNotificationCount: badgeNotificationCount || 0,
});

export const getInitialState = ({
    showHeaderSearch,
    showSearchForm,
    systemMessages,
    systemBadges,
    productCommunications,
    showCollectSsnModal,
}: Partial<Omit<UiState, 'systemBadges' | 'systemMessages'>> & {
    systemBadges?: {
        badges: Record<SystemBadgeType, SystemBadge>;
        notificationCount: number;
    };
    systemMessages?: {
        messages: Record<SystemMessageType, SystemMessage>;
        notificationCount: number;
    };
}) => {
    return nullifyUndefinedProperties({
        categoryDropdownMenu: { open: MODAL_STATE.CLOSED },
        badges: getInitialBadges(
            systemMessages?.notificationCount,
            systemBadges?.notificationCount,
        ),
        itemSettings: {
            open: MODAL_STATE.CLOSED,
            item: {},
        },
        newMessageModal: {
            open: MODAL_STATE.CLOSED,
            itemId: '',
            senderId: '',
            recipientId: '',
            recipientAlias: '',
            itemTitle: '',
            itemImageUrl: '',
        },
        modals: {
            currentModal: null,
        },
        notificationOptinModal: {
            open: MODAL_STATE.CLOSED,
            modalType: null,
            gaLabel: null,
            title: null,
            description: null,
            tagGroups: [],
        },
        profileMenu: { open: MODAL_STATE.CLOSED, redirectUrl: null },
        quickView: {
            open: MODAL_STATE.CLOSED,
            items: [],
            item: null,
            loading: false,
        },
        showHeaderSearch,
        showSearchForm,
        systemMessages: systemMessages?.messages || {},
        systemBadges: systemBadges?.badges || {},
        videoModal: {
            open: MODAL_STATE.CLOSED,
            video: '',
        },
        shareModal: {
            isOpen: false,
            settings: null,
        },
        wishListSideSheet: {
            isOpen: false,
        },
        notificationsSideSheet: {
            isOpen: false,
        },
        productCommunications,
        collectSsnModal: {
            isOpen: !!showCollectSsnModal,
        },
        referralModal: {
            isOpen: false,
        },
        showAdBlockerMessage: false,
    });
};

export const initializeFromInitData =
    (initData: Partial<InitData>) => (dispatch: Dispatch) => {
        const state = getInitialState({
            // Header search should always be available unless startpage,
            // but next has separare state initializer so this should always be true
            showHeaderSearch: true,
            showSearchForm: !!initData.hasSearch,
            systemMessages: cache.read(CACHE_KEYS.SYSTEM_MESSAGES) || {},
            systemBadges: cache.read(CACHE_KEYS.SYSTEM_BADGES) || {},
            productCommunications: initData.productCommunications,
            showCollectSsnModal:
                initData.memberCountryCodeIso2 === 'SE' &&
                !initData.isCompany &&
                !initData.hasSsn,
        });
        dispatch(initialize(state));
    };
